import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FaUsers, FaSearch, FaGraduationCap, FaBaseballBall,
  FaCreditCard, FaTimes, FaIdCard, FaMedal,
  FaExclamationTriangle, FaCheckCircle, FaBan, FaTrashAlt,
  FaUserPlus,
} from "react-icons/fa";
import { useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { PrimaryButton } from "./components/ThemedButton";
import API_BASE from "./apiConfig";
import StudentMaster from "./StudentMaster";
import LaneClientProfile from "./LaneClientProfile";

const BASE  = API_BASE;
const BLUR  = "blur(20px)";
const FONT  = "'Segoe UI', system-ui, sans-serif";

const ST = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; }, blur: BLUR, font: FONT };

// Role badge chips shown in search results
const ROLE_META = {
  student:     { label: "Student",     color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  lane_booker: { label: "Lane Booker", color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  member:      { label: "Member",      color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || { label: role, color: "#6b7280", bg: "rgba(107,114,128,0.12)" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px",
                   background: m.bg, color: m.color, fontFamily: FONT, whiteSpace: "nowrap" }}>
      {m.label}
    </span>
  );
}

// ── Unified client search bar ─────────────────────────────────────────────────
function ClientSearchBar({ onSelect, displayName, onClear }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [query,          setQuery]          = useState("");
  const [results,        setResults]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [inclSuspended,  setInclSuspended]  = useState(false);
  const [inclDeleted,    setInclDeleted]    = useState(false);
  const timerRef = useRef(null);

  // Controlled: if displayName is set externally (person selected), show it
  useEffect(() => {
    if (displayName != null) setQuery(displayName);
  }, [displayName]);

  const search = useCallback((q, suspended, deleted) => {
    clearTimeout(timerRef.current);
    if (!q || q.length < 2) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ q });
        if (suspended) params.set("include_suspended", "true");
        if (deleted)   params.set("include_deleted",   "true");
        const r = await fetch(`${BASE}/lb/search-clients?${params}`);
        const d = await r.json();
        setResults(Array.isArray(d) ? d : (d.clients || d.results || []));
      } catch { setResults([]); }
      setLoading(false);
    }, 220);
  }, []);

  // Re-run search when checkboxes change (if there's already a query)
  useEffect(() => {
    if (query && query.length >= 2) search(query, inclSuspended, inclDeleted);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inclSuspended, inclDeleted]);

  const pick = (p) => {
    setQuery(`${p.first_name} ${p.last_name}`);
    setResults([]);
    onSelect(p);
  };

  const clear = () => {
    setQuery(""); setResults([]);
    if (onClear) onClear();
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div style={{ display: "flex", alignItems: "center",
                    border: `1.5px solid ${T.inputBdr}`, borderRadius: 10,
                    padding: "9px 14px", background: T.inputBg,
                    backdropFilter: BLUR, WebkitBackdropFilter: BLUR }}>
        <FaSearch size={13} style={{ color: T.textMuted, marginRight: 10, flexShrink: 0 }} />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); search(e.target.value, inclSuspended, inclDeleted); }}
          placeholder="Search by name, email…"
          style={{ border: "none", outline: "none", background: "transparent",
                   fontSize: 14, color: T.inputText, width: "100%", fontFamily: FONT }}
        />
        {loading && <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 6 }}>…</span>}
        {query && (
          <button onClick={clear}
            style={{ background: "none", border: "none", cursor: "pointer",
                     color: T.textMuted, padding: "0 0 0 8px", lineHeight: 1 }}>
            <FaTimes size={12} />
          </button>
        )}
      </div>

      {/* Status filter checkboxes */}
      <div style={{ display: "flex", gap: 16, marginTop: 6, paddingLeft: 2 }}>
        {[
          { key: "suspended", label: "Include Suspended", val: inclSuspended, set: setInclSuspended, color: "#ef4444" },
          { key: "deleted",   label: "Include Deleted",   val: inclDeleted,   set: setInclDeleted,   color: "#6b7280" },
        ].map(({ key, label, val, set, color }) => (
          <label key={key} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer",
                                    fontSize: 11, fontFamily: FONT, userSelect: "none",
                                    color: val ? color : T.textMuted }}>
            <input type="checkbox" checked={val} onChange={e => set(e.target.checked)}
              style={{ accentColor: color, width: 13, height: 13 }} />
            {label}
          </label>
        ))}
      </div>

      {results.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
                      background: isDark ? "#1e293b" : "#fff",
                      border: `1px solid ${T.inputBdr}`, borderRadius: 10,
                      boxShadow: "0 8px 32px rgba(0,0,0,0.2)", marginTop: 4,
                      maxHeight: 340, overflowY: "auto" }}>
          {results.map((p, i) => (
            <button key={i} onClick={() => pick(p)}
              style={{ width: "100%", textAlign: "left", background: "none", border: "none",
                       padding: "10px 14px", cursor: "pointer", fontFamily: FONT,
                       borderBottom: i < results.length - 1 ? `1px solid ${T.divider}` : "none",
                       display: "flex", alignItems: "center", gap: 12,
                       transition: "background 0.1s" }}
              onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "#f8fafc"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              {/* Avatar */}
              <div style={{ width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                             background: p.student_id
                               ? `linear-gradient(135deg,${getBrandColors().yellow},#fff3a0)`
                               : "rgba(99,102,241,0.18)",
                             display: "flex", alignItems: "center", justifyContent: "center",
                             fontSize: 13, fontWeight: 800,
                             color: p.student_id ? getBrandColors().blue : "#6366f1" }}>
                {p.first_name?.[0]}{p.last_name?.[0]}
              </div>
              {/* Name + email + roles */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text,
                               whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.first_name} {p.last_name}
                  {p.parent_name && (
                    <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 400, marginLeft: 8 }}>
                      parent: {p.parent_name}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{p.email}</div>
              </div>
              {/* Role chips + status badges */}
              <div style={{ display: "flex", gap: 4, flexShrink: 0, flexWrap: "wrap", maxWidth: 200, justifyContent: "flex-end" }}>
                {(p.roles || []).map(r => <RoleBadge key={r} role={r} />)}
                {p.student_suspended && (
                  <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px",
                                 background: "rgba(239,68,68,0.12)", color: "#ef4444", fontFamily: FONT }}>
                    Suspended
                  </span>
                )}
                {p.client_status === "suspended" && !p.student_suspended && (
                  <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px",
                                 background: "rgba(239,68,68,0.12)", color: "#ef4444", fontFamily: FONT }}>
                    Suspended
                  </span>
                )}
                {p.client_status === "inactive" && (
                  <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px",
                                 background: "rgba(107,114,128,0.15)", color: "#6b7280", fontFamily: FONT }}>
                    Deleted
                  </span>
                )}
                {p.card_last4 && (
                  <span style={{ fontSize: 10, fontWeight: 700, borderRadius: 20, padding: "2px 8px",
                                 background: "rgba(56,189,248,0.12)", color: "#0ea5e9", fontFamily: FONT }}>
                    ••{p.card_last4}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Status Banner — shown when selected person is suspended/deleted ────────────
function StatusBanner({ person, onStatusChanged }) {
  const { isDark } = useTheme(); const T = getT(isDark);
  const [busy,   setBusy]   = useState(false);
  const [reason, setReason] = useState("");

  const isStudent  = !!person.student_id;
  const isCustomer = !!person.client_id;

  // Derive current status
  const studentStatus = person.student_active_status || 1; // 1=Active 2=Suspended 3=Deleted
  const clientStatus  = person.client_status || "active";  // active suspended inactive

  const studentSuspended = studentStatus === 2;
  const studentDeleted   = studentStatus === 3;
  const clientSuspended  = clientStatus === "suspended";
  const clientDeleted    = clientStatus === "inactive";

  const isSuspended = studentSuspended || clientSuspended;
  const isDeleted   = studentDeleted   || clientDeleted;

  if (!isSuspended && !isDeleted) return null;

  const bannerColor  = isDeleted ? "#6b7280" : "#ef4444";
  const bannerBg     = isDeleted ? "rgba(107,114,128,0.08)" : "rgba(239,68,68,0.07)";
  const bannerBorder = isDeleted ? "rgba(107,114,128,0.25)" : "rgba(239,68,68,0.25)";
  const statusLabel  = isDeleted ? "Deleted" : "Suspended";

  const callApi = async (type, newStatus) => {
    setBusy(true);
    try {
      if (type === "student") {
        await fetch(`${BASE}/studentupdate/${person.student_id}/set-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ active_status: newStatus, changed_by: "admin" }),
        });
      } else {
        await fetch(`${BASE}/lb/clients/${person.client_id}/set-status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus, changed_by: "admin", reason }),
        });
      }
      onStatusChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    if (isStudent) await callApi("student", 1);
    if (isCustomer && !isStudent) await callApi("client", "active");
    // If both: reactivate both
    if (isStudent && isCustomer) await callApi("client", "active");
  };

  const handleSuspend = async () => {
    if (isStudent) await callApi("student", 2);
    if (isCustomer) await callApi("client", "suspended");
  };

  const handleDelete = async () => {
    if (!window.confirm(`Mark ${person.first_name} ${person.last_name} as Deleted?`)) return;
    if (isStudent) await callApi("student", 3);
    if (isCustomer) await callApi("client", "inactive");
  };

  return (
    <div style={{ marginBottom: 16, padding: "14px 18px", borderRadius: 12,
                  background: bannerBg, border: `1.5px solid ${bannerBorder}`,
                  display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
      <FaExclamationTriangle size={16} color={bannerColor} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: bannerColor, fontFamily: FONT }}>
          {person.first_name} {person.last_name} — Account {statusLabel}
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
          {isStudent  && `Student status: ${studentStatus === 2 ? "Suspended" : studentStatus === 3 ? "Deleted" : "Active"}`}
          {isStudent && isCustomer && " · "}
          {isCustomer && `Customer status: ${clientStatus}`}
        </div>
      </div>

      {/* Reason field for customer status changes */}
      {isCustomer && (
        <input value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Reason (optional)"
          style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${T.inputBdr}`,
                   background: T.inputBg, color: T.inputText, fontSize: 12,
                   fontFamily: FONT, outline: "none", width: 180 }} />
      )}

      {/* Action buttons */}
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        {/* Reactivate — always shown when suspended or deleted */}
        <button disabled={busy} onClick={handleReactivate}
          style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                   fontSize: 12, fontWeight: 700, fontFamily: FONT,
                   background: "rgba(16,185,129,0.12)", color: "#10b981",
                   display: "flex", alignItems: "center", gap: 6, opacity: busy ? 0.6 : 1 }}>
          <FaCheckCircle size={12} /> {busy ? "…" : "Reactivate"}
        </button>

        {/* Suspend — shown only when deleted (to move to suspended state) */}
        {isDeleted && (
          <button disabled={busy} onClick={handleSuspend}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                     fontSize: 12, fontWeight: 700, fontFamily: FONT,
                     background: "rgba(239,68,68,0.1)", color: "#ef4444",
                     display: "flex", alignItems: "center", gap: 6, opacity: busy ? 0.6 : 1 }}>
            <FaBan size={12} /> Suspend
          </button>
        )}

        {/* Delete — shown only when suspended */}
        {isSuspended && !isDeleted && (
          <button disabled={busy} onClick={handleDelete}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", cursor: "pointer",
                     fontSize: 12, fontWeight: 700, fontFamily: FONT,
                     background: "rgba(107,114,128,0.1)", color: "#6b7280",
                     display: "flex", alignItems: "center", gap: 6, opacity: busy ? 0.6 : 1 }}>
            <FaTrashAlt size={11} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ T }) {
  return (
    <div style={{ background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                  border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh,
                  padding: "52px 40px", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
      <div style={{ width: 64, height: 64, borderRadius: "50%",
                    background: `linear-gradient(135deg,${getBrandColors().yellow},#fff3a0)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    margin: "0 auto 18px", boxShadow: "0 4px 16px rgba(255,200,0,0.28)" }}>
        <FaSearch size={24} color={getBrandColors().blue} />
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: getBrandColors().blue, marginBottom: 10, fontFamily: FONT }}>
        Search for a Person
      </div>
      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.7, fontFamily: FONT }}>
        Type a name or email above to find a student or lane customer.
        <br />
        Results show their roles — <span style={{ color: "#10b981", fontWeight: 700 }}>Student</span>,{" "}
        <span style={{ color: "#6366f1", fontWeight: 700 }}>Lane Booker</span>, or{" "}
        <span style={{ color: "#f59e0b", fontWeight: 700 }}>Member</span>.
        <br /><br />
        Selecting a <strong style={{ color: getBrandColors().blue }}>student</strong> opens the full 360° student profile.
        <br />
        Selecting a <strong style={{ color: "#6366f1" }}>lane customer</strong> opens their booking &amp; billing profile.
      </div>
    </div>
  );
}


// ── Main component ────────────────────────────────────────────────────────────
export default function ClientMaster({ currentUserId }) {
  const { isDark } = useTheme(); const T = getT(isDark);

  const [selected,     setSelected]     = useState(null);
  const [displayName,  setDisplayName]  = useState(undefined);
  const [personStatus, setPersonStatus] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  // For a person who is BOTH a student and a lane customer, the default view
  // is the student's 360° profile — but that leaves no way to reach the
  // lane client's own Membership tab (assign/change plan, cancel, extras)
  // once they've been linked, since this component would otherwise never
  // render <LaneClientProfile> for them at all. This toggle lets an admin
  // flip to the lane customer view for exactly that case, without changing
  // behavior for anyone who is only a student or only a lane customer.
  const [viewAsLane,   setViewAsLane]   = useState(false);

  const handleSelect = (person) => {
    setSelected(person);
    setDisplayName(`${person.first_name} ${person.last_name}`);
    setPersonStatus({
      student_active_status: person.student_active_status || 1,
      client_status:         person.client_status || "active",
    });
    setViewAsLane(false);
  };

  const handleClear = () => {
    setSelected(null);
    setDisplayName(undefined);
    setPersonStatus(null);
    setViewAsLane(false);
  };

  // After a status change, re-fetch live status for this person
  const handleStatusChanged = useCallback(async () => {
    if (!selected) return;
    try {
      const params = new URLSearchParams({ q: selected.email || `${selected.first_name} ${selected.last_name}` });
      params.set("include_suspended", "true");
      params.set("include_deleted", "true");
      const d = await fetch(`${BASE}/lb/search-clients?${params}`).then(r => r.json());
      const list = d.clients || [];
      const updated = list.find(p =>
        (selected.student_id && p.student_id === selected.student_id) ||
        (selected.client_id  && p.client_id  === selected.client_id)
      );
      if (updated) {
        setPersonStatus({
          student_active_status: updated.student_active_status || 1,
          client_status:         updated.client_status || "active",
        });
      }
    } catch {}
  }, [selected]);

  // Determine view mode from the selected person
  const isStudent   = selected?.student_id != null;
  const isDualRole  = isStudent && selected?.client_id != null;
  const isCustomer  = (selected?.client_id != null && !isStudent) || (isDualRole && viewAsLane);
  const showStudentProfile = isStudent && !(isDualRole && viewAsLane);

  // Merged person object with live status
  const personWithStatus = selected && personStatus
    ? { ...selected, ...personStatus }
    : selected;

  // ── Create Student inline view ─────────────────────────────────────────────
  if (showCreate) {
    return (
      <div style={{ minHeight: "100%", padding: "20px 28px 48px", boxSizing: "border-box", fontFamily: FONT }}>
        {/* Back button */}
        <button onClick={() => setShowCreate(false)}
          style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18,
                   background: "none", border: "none", cursor: "pointer",
                   color: getBrandColors().blue, fontSize: 13, fontWeight: 700, fontFamily: FONT, padding: 0 }}>
          <span style={{ fontSize: 16 }}>←</span> Back to Client Profile
        </button>
        <StudentMaster
          currentUserId={currentUserId}
          standalone={true}
          defaultCreateMode={true}
          onCreateDone={() => setShowCreate(false)}
        />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100%", padding: "20px 28px 48px", boxSizing: "border-box",
                  fontFamily: FONT }}>

      {/* ── Page header ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>

        {/* Icon + title */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          <PageHeaderIcon icon={<FaUsers size={20} color={getBrandColors().blueDark} />} />
          <h2 style={{ fontSize: 22, fontWeight: 800, color: getBrandColors().blue, margin: 0,
                       letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
            Client Profile
          </h2>
        </div>

        {/* Unified search — grows to fill middle */}
        <div style={{ flex: 1, minWidth: 220, maxWidth: 520 }}>
          <ClientSearchBar
            onSelect={handleSelect}
            displayName={selected ? displayName : undefined}
            onClear={handleClear}
          />
        </div>

        {/* Create Student button */}
        <PrimaryButton onClick={() => setShowCreate(true)}
          style={{ fontFamily: FONT, flexShrink: 0 }}>
          <FaUserPlus size={13} /> Create Student
        </PrimaryButton>

        {/* Context badge — shows what type of profile is open */}
        {selected && (
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8,
                        padding: "7px 14px", borderRadius: 10, flexShrink: 0,
                        background: isStudent
                          ? `linear-gradient(135deg,${getBrandColors().yellow}22,${getBrandColors().yellow}11)`
                          : "rgba(99,102,241,0.1)",
                        border: isStudent
                          ? `1px solid ${getBrandColors().yellow}55`
                          : "1px solid rgba(99,102,241,0.25)" }}>
            {isStudent
              ? <><FaGraduationCap size={13} color={getBrandColors().blue}/> <span style={{ fontSize: 12, fontWeight: 700, color: getBrandColors().blue }}>Student</span></>
              : <><FaIdCard size={13} color="#6366f1"/>   <span style={{ fontSize: 12, fontWeight: 700, color: "#6366f1" }}>Customer</span></>
            }
            {(selected.roles || []).map(r => <RoleBadge key={r} role={r} />)}
          </div>
        )}
      </div>

      {/* ── Status banner — reactivate / suspend / delete from here ── */}
      {personWithStatus && (
        <StatusBanner person={personWithStatus} onStatusChanged={handleStatusChanged} />
      )}

      {/* ── Dual-role switcher — shown above whichever profile is active, so
             an admin can reach the lane client's own Membership tab (plan
             assign/change/cancel, extras) that's otherwise unreachable once
             a lane client is linked to a student. The student side only
             ever shows that membership read-only (Billing Cycle tab), by
             design, to avoid two screens independently mutating the same
             billing relationship — this toggle is how you get to the real
             one. ── */}
      {isDualRole && (
        <div style={{ marginBottom: 12, padding: "9px 14px", borderRadius: 9, fontSize: 12,
                      background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.2)",
                      color: "#6366f1", fontFamily: FONT, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <FaIdCard size={11} />
          {viewAsLane
            ? <>Viewing the lane customer profile (Client ID: CLI-{String(selected.client_id).padStart(5,"0")}) — this person is also a student.</>
            : <>This person is also a lane customer (Client ID: CLI-{String(selected.client_id).padStart(5,"0")}).</>}
          <button onClick={() => setViewAsLane(v => !v)}
            style={{ marginLeft: "auto", padding: "4px 12px", borderRadius: 7, border: "1px solid #6366f1",
                     background: "transparent", color: "#6366f1", fontSize: 11, fontWeight: 700,
                     cursor: "pointer", fontFamily: FONT, whiteSpace: "nowrap" }}>
            {viewAsLane ? "← Back to Student Profile" : "Manage Lane Membership →"}
          </button>
        </div>
      )}

      {/* ── Student profile (embedded StudentMaster) ── */}
      {showStudentProfile && (
        <StudentMaster
          currentUserId={currentUserId}
          defaultStudentId={selected.student_id}
          standalone={false}
        />
      )}

      {/* ── Lane customer profile ── */}
      {isCustomer && (
        <LaneClientProfile
          clientId={selected.client_id}
        />
      )}

      {/* ── Empty state ── */}
      {!selected && <EmptyState T={T} />}
    </div>
  );
}
